class GutTimelineModule extends HTMLElement {
  connectedCallback() {
    this.buttons = Array.from(this.querySelectorAll('[data-day-button]'));
    this.panels = Array.from(this.querySelectorAll('[data-day-panel]'));
    this.mediaItems = Array.from(this.querySelectorAll('[data-day-media]'));
    this.track = this.querySelector('[data-track]');
    this.trackFill = this.querySelector('[data-track-fill]');
    this.trackHandle = this.querySelector('[data-track-handle]');
    this.autoplayTimer = null;
    this.currentIndex = 0;
    this.days = this.querySelector('.gut-timeline__days');

    // Desktop: labels at their day-number ratio (timeline). Phones: equal
    // gaps, laid out by CSS. Re-laid out when the breakpoint flips.
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.layoutLabels();
    this.desktopQuery.addEventListener('change', () => {
      this.layoutLabels();
      this.previewRatio(this.ratios[this.currentIndex]);
    });

    this.buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.stopAutoplay();
        this.activate(index);
      });
    });

    this.bindTrack();

    // Defer the first activation until the module is visible so the
    // biomarker bars animate from zero when the user reaches it.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            this.activate(0);
            this.startAutoplay();
          }
        },
        { threshold: 0.3 }
      );
      observer.observe(this);
    } else {
      this.activate(0);
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
  }

  get scaled() {
    return this.desktopQuery.matches;
  }

  // Positions proportional to the actual day numbers (1, 3, 14, 30, 45…),
  // with a minimum gap so close days ("Day 1"/"Day 3") don't overlap.
  dayRatios() {
    const count = this.buttons.length;
    const even = this.buttons.map((_, i) => i / Math.max(count - 1, 1));
    const days = this.buttons.map((button) => {
      const match = button.textContent.match(/\d+/);
      return match ? parseInt(match[0], 10) : NaN;
    });
    if (days.some(Number.isNaN) || count < 2) return even;
    const min = days[0];
    const span = Math.max(days[count - 1] - min, 1);
    const ratios = days.map((day) => (day - min) / span);
    const minGap = 0.09;
    for (let i = 1; i < ratios.length; i += 1) {
      ratios[i] = Math.max(ratios[i], ratios[i - 1] + minGap);
    }
    const last = ratios[ratios.length - 1];
    return ratios.map((ratio) => ratio / last);
  }

  layoutLabels() {
    const scaled = this.scaled;
    if (this.days) this.days.classList.toggle('gut-timeline__days--scaled', scaled);
    const ratios = scaled ? this.dayRatios() : [];
    this.buttons.forEach((button, index) => {
      button.style.left = scaled ? `${ratios[index] * 100}%` : '';
    });
  }

  // Handle positions: the day ratios on desktop; on phones the labels are
  // laid out by CSS with equal gaps, so the handle snaps to the centre of
  // each one, with the first and last pinned to the track ends. Measured on
  // demand (a handful of rects) so it stays right after resizes and late
  // font swaps without an observer.
  get ratios() {
    if (this.scaled) return this.dayRatios();
    const count = this.buttons.length;
    const even = this.buttons.map((_, i) => i / Math.max(count - 1, 1));
    if (!this.track || count < 2) return even;
    const rect = this.track.getBoundingClientRect();
    if (!rect.width) return even;
    return this.buttons.map((button, i) => {
      if (i === 0) return 0;
      if (i === count - 1) return 1;
      const box = button.getBoundingClientRect();
      const ratio = (box.left + box.width / 2 - rect.left) / rect.width;
      return Math.min(Math.max(ratio, 0), 1);
    });
  }

  startAutoplay() {
    if (this.dataset.autoplay !== 'true' || this.buttons.length < 2) return;
    const delay = (parseInt(this.dataset.autoplaySpeed, 10) || 5) * 1000;
    this.autoplayTimer = setInterval(() => {
      if (this.currentIndex >= this.buttons.length - 1) {
        this.stopAutoplay();
        return;
      }
      this.activate(this.currentIndex + 1);
    }, delay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  bindTrack() {
    if (!this.track || this.buttons.length < 2) return;

    const ratioFromEvent = (event) => {
      const rect = this.track.getBoundingClientRect();
      return Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    };
    const nearestIndex = (ratio) => {
      const ratios = this.ratios;
      let best = 0;
      ratios.forEach((r, i) => {
        if (Math.abs(r - ratio) < Math.abs(ratios[best] - ratio)) best = i;
      });
      return best;
    };

    let dragging = false;

    this.track.addEventListener('pointerdown', (event) => {
      dragging = true;
      this.stopAutoplay();
      this.track.setPointerCapture(event.pointerId);
      this.classList.add('is-dragging');
      this.previewRatio(ratioFromEvent(event));
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      this.previewRatio(ratioFromEvent(event));
    });

    const release = (event) => {
      if (!dragging) return;
      dragging = false;
      this.classList.remove('is-dragging');
      this.activate(nearestIndex(ratioFromEvent(event)));
    };
    this.track.addEventListener('pointerup', release);
    this.track.addEventListener('pointercancel', release);
  }

  previewRatio(ratio) {
    const progress = ratio * 100;
    if (this.trackFill) this.trackFill.style.width = `${progress}%`;
    if (this.trackHandle) this.trackHandle.style.left = `${progress}%`;
  }

  activate(index) {
    this.currentIndex = index;
    const setActive = (items, activeIndex, className) => {
      items.forEach((item, i) => item.classList.toggle(className, i === activeIndex));
    };
    setActive(this.panels, index, 'is-active');
    setActive(this.mediaItems, index, 'is-active');
    this.buttons.forEach((button, i) => {
      button.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      // Days at or before the active one render in the accent color.
      button.classList.toggle('is-passed', i <= index);
    });

    this.previewRatio(this.ratios[index]);

    const activeButton = this.buttons[index];
    if (!activeButton) return;
    for (let i = 1; i <= 3; i += 1) {
      const value = parseInt(activeButton.getAttribute(`data-biomarker-${i}`), 10) || 0;
      const fill = this.querySelector(`[data-biomarker-fill="${i}"]`);
      const label = this.querySelector(`[data-biomarker-value="${i}"]`);
      if (fill) fill.style.width = `${value}%`;
      if (label) label.textContent = `${value}%`;
    }
  }
}

if (!customElements.get('gut-timeline-module')) {
  customElements.define('gut-timeline-module', GutTimelineModule);
}
